/**
 * Password rules — NIST SP 800-63B shaped, shared by the API (enforcement) and
 * the login/reset pages (the strength bar and inline hints), so the two can
 * never disagree about what "acceptable" means.
 *
 * The rules are deliberately about LENGTH and GUESSABILITY, not character
 * classes. 800-63B §5.1.1.2 explicitly drops composition rules ("must contain
 * a symbol") because they push people toward `Password1!` while blocking
 * genuinely strong passphrases. What it keeps is a minimum length and a check
 * against known-bad values, which is what this file implements.
 *
 * The blocklist is intentionally tiny. A real breach-corpus check (HIBP's
 * k-anonymity range API) is the right answer at scale, but it is a third-party
 * network call on the one path that must never be flaky, for a three-person
 * user set who are being handed the rules in a form that shows them live.
 */

/** 800-63B floor is 8; 12 is the modern operational minimum. */
export const PASSWORD_MIN_LENGTH = 12;

/**
 * Upper bound. Not a security control — scrypt runs over a fixed-length HMAC
 * digest so length costs nothing (see `auth/password.ts`) — it exists so a
 * request body can't carry a megabyte of "password".
 */
export const PASSWORD_MAX_LENGTH = 256;

/**
 * The short blocklist. Lowercased, compared after stripping nothing: these are
 * checked as the WHOLE password, plus a separate substring check for the
 * user's own email local part (the single most common real-world choice).
 */
const COMMON_PASSWORDS: readonly string[] = [
  'password',
  'password1',
  'password123',
  'password1234',
  'passw0rd123',
  '123456789012',
  '1234567890123',
  'qwertyuiop12',
  'iloveyou1234',
  'letmein12345',
  'welcome12345',
  'adminadmin12',
  'trustno1trustno1',
  'coffman12345',
  'coffmantm123',
  'hoffman12345',
  'micron123456',
  'excavation12',
];

export type PasswordProblem =
  | 'too_short'
  | 'too_long'
  | 'contains_email'
  | 'too_common'
  | 'whitespace_only';

export interface PasswordCheck {
  ok: boolean;
  problems: PasswordProblem[];
  /** One sentence, safe to render directly. Empty when `ok`. */
  message: string;
}

/**
 * Validate a candidate password. `email` is optional so the reset page can
 * check before it knows who the token belongs to; when it IS known, the local
 * part (everything before `@`, ≥3 chars) may not appear in the password.
 */
export function checkPassword(password: string, email?: string | undefined): PasswordCheck {
  const problems: PasswordProblem[] = [];

  if (typeof password !== 'string' || password.trim().length === 0) {
    problems.push('whitespace_only');
    return { ok: false, problems, message: PROBLEM_MESSAGES.whitespace_only };
  }
  if (password.length < PASSWORD_MIN_LENGTH) problems.push('too_short');
  if (password.length > PASSWORD_MAX_LENGTH) problems.push('too_long');

  const lower = password.toLowerCase();
  if (COMMON_PASSWORDS.includes(lower)) problems.push('too_common');

  const localPart = typeof email === 'string' ? email.split('@')[0]?.toLowerCase() ?? '' : '';
  if (localPart.length >= 3 && lower.includes(localPart)) problems.push('contains_email');

  const first = problems[0];
  return {
    ok: problems.length === 0,
    problems,
    message: first ? PROBLEM_MESSAGES[first] : '',
  };
}

const PROBLEM_MESSAGES: Record<PasswordProblem, string> = {
  too_short: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
  too_long: `Password must be ${PASSWORD_MAX_LENGTH} characters or fewer.`,
  contains_email: 'Password must not contain your email address.',
  too_common: 'That password is too common. Choose something less guessable.',
  whitespace_only: 'Enter a password.',
};

/**
 * 0-3 strength bucket for the 4px bar on the set-password screens. Purely
 * advisory — `checkPassword` is the gate, and a "fair" password is accepted.
 * Length dominates on purpose (it is the only input that reliably tracks
 * guessing cost); variety is a weak secondary signal, not a requirement.
 */
export function passwordStrength(password: string): 0 | 1 | 2 | 3 {
  if (!password || password.length < PASSWORD_MIN_LENGTH) return 0;
  const variety =
    (/[a-z]/.test(password) ? 1 : 0) +
    (/[A-Z]/.test(password) ? 1 : 0) +
    (/[0-9]/.test(password) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(password) ? 1 : 0);
  if (password.length >= 20 || (password.length >= 16 && variety >= 3)) return 3;
  if (password.length >= 14 || variety >= 2) return 2;
  return 1;
}
